import { AbcCore, CollectionHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  NavigationGroupViewModel,
  NavigationLayoutViewModel,
  NavigationNodeBaseViewModel,
  NavigationViewModel,
} from '../core/navigation/navigation.viewModel';
import { LayoutViewModel } from '../core/viewModels/layout.viewModel';
import { IArenaLayoutPluginInfo, IArenaLayoutPluginNotificationInfo } from './arena-layout-plugin.model';
import { IArenaPlugin, IArenaPluginInfo } from './arena-plugin.model';
import { ICoreDataEntity, Query, QueryHelper, QueryObjectsModel } from '@barthauer/abc/core/data';
import { NavigationNodeBase } from '../core/navigation/navigation.model';
import { AdditionalHigonAction } from '../core/higon/higon.model';
import { AdditionalMenuItem } from '../core/menu/menu.model';
import { CRUDPermissions } from '../core/models/layout.model';

class ArenaLayoutPluginContext {
  public state: ArenaLayoutPluginState;
  public layoutInfos: IArenaLayoutPluginInfo[];
  public notificationInfos: IArenaLayoutPluginNotificationInfo[];
}

class ArenaLayoutPluginState {
  constructor(public readonly navigationLayout: NavigationLayoutViewModel) {}

  public async update(infos: IArenaLayoutPluginInfo[]): Promise<ArenaLayoutPluginContext> {
    const getAdditionalHigonActions: (() => Promise<AdditionalHigonAction[]>)[] = [];
    const getAdditionalMenuItems: (() => Promise<AdditionalMenuItem[]>)[] = [];
    const getCRUDPermissions: (() => Promise<CRUDPermissions>)[] = [];
    const notificationInfos: IArenaLayoutPluginNotificationInfo[] = [];
    if (infos) {
      for (const info of infos) {
        if (info.getNotificationInfo) {
          const notificationInfo = await info.getNotificationInfo();
          if (notificationInfo) {
            notificationInfos.push(notificationInfo);
          }
        }
        if (info.getAdditionalHigonActions) {
          getAdditionalHigonActions.push(() => info.getAdditionalHigonActions());
        }
        if (info.getAdditionalMenuItems) {
          getAdditionalMenuItems.push(() => info.getAdditionalMenuItems());
        }
        if (info.getCRUDPermissions) {
          getCRUDPermissions.push(() => info.getCRUDPermissions());
        }
      }
    }
    return {
      state: this,
      layoutInfos: infos,
      notificationInfos: notificationInfos,
    };
  }

  public async reset(): Promise<void> {}
}

class ArenaPluginState {
  private layoutCache: Map<NavigationLayoutViewModel, ArenaLayoutPluginState> = new Map();

  constructor(public readonly info: IArenaPluginInfo) {}

  public async updateLayout(
    navigationLayout: NavigationLayoutViewModel,
    infos: IArenaLayoutPluginInfo[],
  ): Promise<ArenaLayoutPluginContext> {
    let state = this.layoutCache.get(navigationLayout);
    if (!state) {
      state = new ArenaLayoutPluginState(navigationLayout);
      this.layoutCache.set(navigationLayout, state);
    }
    return await state.update(infos);
  }

  public async reset(): Promise<void> {
    for (const e of this.layoutCache) {
      await e[1].reset();
    }
    this.layoutCache.clear();
  }
}

export interface IArenaLayoutPluginActions {
  findAdditionalHigonActions(): Promise<AdditionalHigonAction[]>;
  findAdditionalMenuItems(): Promise<AdditionalMenuItem[]>;
  findCRUDPermissions(): Promise<CRUDPermissions>;
  notifyCurrentObject(entity: ICoreDataEntity): Promise<void>;
}

export class ArenaPluginManager {
  private static readonly DefaultBadgeUpdateInterval = 60000;
  private static readonly DefaultBadgeResetTimeout = 5000;
  private static readonly DefaultBadgeCheckTimeout = 500;

  constructor(
    private abcCore: AbcCore,
    private isArenaBusy: () => boolean,
  ) {}

  private pluginCache: Map<IArenaPlugin, ArenaPluginState> = new Map();
  private _plugins: IArenaPlugin[];
  private contexts: ArenaLayoutPluginContext[] = [];
  get plugins(): IArenaPlugin[] {
    return this._plugins;
  }
  set plugins(value: IArenaPlugin[]) {
    if (this._plugins !== value) {
      this._plugins = value;
      this.update(this.navigationDocument, this._layoutFactory);
    }
  }
  private _navigationDocument: NavigationViewModel;
  get navigationDocument(): NavigationViewModel {
    return this._navigationDocument;
  }
  private _layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>;
  get layoutFactory(): (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel> {
    return this._layoutFactory;
  }

  public async update(
    navigationDocument: NavigationViewModel,
    layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>,
  ): Promise<void> {
    if (!this.plugins || !navigationDocument) {
      return;
    }
    this._navigationDocument = navigationDocument;
    this._layoutFactory = layoutFactory;
    const visited: IArenaPlugin[] = [];
    this.contexts = [];
    for (const plugin of this.plugins) {
      plugin.updateNotificationInfos = () => this.updateNotificationInfos();
      const info = await plugin.update(navigationDocument);
      if (info) {
        let state = this.pluginCache.get(plugin);
        if (state?.info !== info) {
          state?.reset();
          state = new ArenaPluginState(info);
          this.pluginCache.set(plugin, state);
        }
        this.contexts.push(...(await this.ensureLayoutPlugins(state, navigationDocument.groups, layoutFactory)));
        visited.push(plugin);
      }
    }
    for (const key of Array.from(this.pluginCache.keys())) {
      if (!visited.includes(key)) {
        this.pluginCache.delete(key);
      }
    }

    if (this.contexts.find((_) => _.notificationInfos?.find((_) => _.filter))) {
    }
  }

  private async ensureLayoutPlugins(
    state: ArenaPluginState,
    nodes: NavigationNodeBaseViewModel[],
    layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>,
  ): Promise<ArenaLayoutPluginContext[]> {
    const contexts: ArenaLayoutPluginContext[] = [];
    if (nodes) {
      for (const ly of nodes) {
        if (ly instanceof NavigationLayoutViewModel) {
          const plugins = await state.info.getLayoutPlugins(ly);
          if (plugins) {
            const infos: IArenaLayoutPluginInfo[] = [];
            for (const plugin of plugins) {
              const info = await plugin.update(ly, layoutFactory);
              if (info) {
                infos.push(info);
              }
            }
            const context = await state.updateLayout(ly, infos);
            if (context) {
              contexts.push(context);
            }
          }
        } else if (ly instanceof NavigationGroupViewModel) {
          contexts.push(...(await this.ensureLayoutPlugins(state, ly.nodes, layoutFactory)));
        }
      }
    }
    return contexts;
  }

  public async activateLayout(layoutPath: string): Promise<IArenaLayoutPluginActions> {
    const contexts: ArenaLayoutPluginContext[] = [];
    for (const ctx of this.contexts) {
      if (ctx.state.navigationLayout.fullName === layoutPath) {
        contexts.push(ctx);
      }
    }
    const layoutInfos = contexts.map((_) => _.layoutInfos);
    return {
      findAdditionalHigonActions: () =>
        this.combineLayoutInfoItems(
          layoutInfos,
          (_) => () => _.getAdditionalHigonActions(),
          (lists) => this.mergeArrays(lists),
        ),
      findAdditionalMenuItems: () =>
        this.combineLayoutInfoItems(
          layoutInfos,
          (_) => () => _.getAdditionalMenuItems(),
          (lists) => this.mergeArrays(lists),
        ),
      findCRUDPermissions: () =>
        this.combineLayoutInfoItems(
          layoutInfos,
          (_) => () => _.getCRUDPermissions(),
          (lists: CRUDPermissions[]) => this.mergeCRUDPermissions(lists),
        ),
      notifyCurrentObject: (entity) =>
        this.combineLayoutInfoItems(
          layoutInfos,
          (_) => async () => {
            await this.notifyCurrentObject(layoutPath, entity);
            return await _.notifyCurrentObject(entity);
          },
          undefined,
        ),
    };
  }

  private async combineLayoutInfoItems<T>(
    layoutInfoLists: IArenaLayoutPluginInfo[][],
    getItemFunc: (layoutInfo: IArenaLayoutPluginInfo) => () => Promise<T>,
    mergeAction: (items: T[]) => T,
  ): Promise<T> {
    const items: T[] = [];
    if (layoutInfoLists && getItemFunc) {
      for (const layoutInfoList of layoutInfoLists) {
        for (const layoutInfo of layoutInfoList) {
          const func = getItemFunc(layoutInfo);
          if (func) {
            const item = await func();
            if (item) {
              items.push(item);
            }
          }
        }
      }
    }
    if (mergeAction) {
      return mergeAction(items);
    }
    return undefined;
  }

  private mergeArrays<T>(lists: T[][]): T[] {
    return CollectionHelper.distinctValues(CollectionHelper.flatten(lists));
  }

  private mergeCRUDPermissions(permissions: CRUDPermissions[]): CRUDPermissions {
    const result: CRUDPermissions = new CRUDPermissions();
    if (permissions) {
      for (const p of permissions) {
        if (p.DenyDelete) {
          result.DenyDelete = true;
        } else if (p.DenyDelete === false) {
          p.DenyDelete = false;
        }
        if (p.DenyInsert) {
          result.DenyInsert = true;
        } else if (p.DenyInsert === false) {
          p.DenyInsert = false;
        }
        if (p.DenyUpdate) {
          result.DenyUpdate = true;
        } else if (p.DenyUpdate === false) {
          p.DenyUpdate = false;
        }
        if (p.IsReadOnly) {
          result.IsReadOnly = true;
        } else if (p.IsReadOnly === false) {
          p.IsReadOnly = false;
        }
      }
    }
    return result;
  }

  private _notificationInfosTimers: NodeJS.Timeout[] = [];
  private _notificationActions: Map<
    string,
    {
      navigationLayout: NavigationLayoutViewModel;
      caption: string;
      resetTimeout: number;
      resetAction: (entity: ICoreDataEntity) => Promise<void>;
    }[]
  > = new Map();
  public async updateNotificationInfos(): Promise<void> {
    await this.disableNotificationInfos();
    const intervalMap: Map<number, [ArenaLayoutPluginState, IArenaLayoutPluginNotificationInfo][]> = new Map();
    for (const context of this.contexts) {
      if (context.notificationInfos) {
        for (const info of context.notificationInfos) {
          const interval = info.refreshInterval ?? ArenaPluginManager.DefaultBadgeUpdateInterval;
          let items = intervalMap.get(interval);
          if (!items) {
            items = [];
            intervalMap.set(interval, items);
          }
          items.push([context.state, info]);
        }
      }
    }

    for (const e of intervalMap) {
      await this.updateNotificationInfosCore(e[1]);
      this._notificationInfosTimers.push(
        setInterval(async () => {
          await this.updateNotificationInfosCore(e[1]);
        }, e[0]),
      );
    }
  }

  private async updateNotificationInfosCore(
    layoutInfos: [ArenaLayoutPluginState, IArenaLayoutPluginNotificationInfo][],
  ): Promise<void> {
    const byObjectType: Map<string, [ArenaLayoutPluginState, IArenaLayoutPluginNotificationInfo][]> = new Map();
    const visitedLayouts: NavigationLayoutViewModel[] = [];
    for (const item of layoutInfos) {
      const state = item[0];
      const objectType = state.navigationLayout.objectType;
      if (objectType && this.isLayoutVisible(state.navigationLayout)) {
        let list = byObjectType.get(objectType);
        if (!list) {
          list = [];
          byObjectType.set(objectType, list);
        }
        list.push([state, item[1]]);
        visitedLayouts.push(state.navigationLayout);
      }
    }

    const resetActionsMap: Map<string, { timeout: number; action: (entity: ICoreDataEntity) => Promise<void> }> =
      new Map();
    for (const item of byObjectType) {
      const objectType = item[0];
      const list = item[1];
      const query = new QueryObjectsModel();
      query.ObjectQueries = [];
      const layoutMap: Map<NavigationLayoutViewModel, string> = new Map();
      const layoutNameMap: Map<string, NavigationLayoutViewModel> = new Map();
      for (const [state, info] of list) {
        let name = layoutMap.get(state.navigationLayout);
        const q = new Query();
        q.ObjectType = objectType;
        query.ObjectQueries.push(q);
        if (!name) {
          name = 'L' + layoutMap.size;
          layoutMap.set(state.navigationLayout, name);
          layoutNameMap.set(name, state.navigationLayout);
        }
        const ly = await info.info.plugin.getLayout();
        q.OPath = QueryHelper.combineFilters([ly.oPath, state.navigationLayout.nodeFilter, info.filter]);
        q.Columns = [
          { Name: 'Id', OPath: 'Id' },
          { Name: 'Layout', OPath: "'" + name + "'" },
          { Name: 'Caption', OPath: "'" + info.caption + "'" },
        ];
        if (info.resetNotificationAction) {
          resetActionsMap.set(info.caption, {
            timeout: info.resetNotificationTimeout,
            action: info.resetNotificationAction,
          });
        }
      }
      const resultArray = ConvertHelper.arrayBufferToObjectArray(
        await this.abcCore.data.executeReadObjectsQuery(query, true, false).toPromise(),
      ) as [{ Id: string; Layout: string; Caption: string }];
      const found: Map<
        string,
        [
          NavigationLayoutViewModel,
          { resetTimeout: number; resetAction: (entity: ICoreDataEntity) => Promise<void>; caption: string }[],
        ]
      > = new Map();
      if (resultArray) {
        for (const result of resultArray) {
          let item = found.get(result.Id);
          const navigationLayout = layoutNameMap.get(result.Layout);
          if (!item) {
            item = [navigationLayout, []];
            found.set(result.Id, item);
          }
          if (item[0] === navigationLayout) {
            const actionInfo = resetActionsMap.get(result.Caption);
            if (actionInfo) {
              item[1].push({
                resetTimeout: actionInfo.timeout,
                resetAction: actionInfo.action,
                caption: result.Caption,
              });
            }
          }
        }
      }
      await this.checkResetBadgeForCurrentObject();
      const notificationActions = new Map(this._notificationActions);
      notificationActions.clear();
      for (const navigationLayout of list.map((e) => e[0].navigationLayout)) {
        let badgeValue: number = 0;
        const badgeInfos: Map<string, string[]> = new Map();
        for (const [id, [ly, items]] of found) {
          if (ly === navigationLayout) {
            badgeValue++;
            for (const item of items) {
              let ids = badgeInfos.get(item.caption);
              if (!ids) {
                ids = [];
                badgeInfos.set(item.caption, ids);
              }
              ids.push(id);
              let actions = notificationActions.get(id);
              if (!actions) {
                actions = [];
                notificationActions.set(id, actions);
              }
              actions.push({
                navigationLayout: ly,
                caption: item.caption,
                resetTimeout: item.resetTimeout,
                resetAction: item.resetAction,
              });
            }
          }
        }
        navigationLayout.badgeInfos = badgeInfos;
      }
      this._notificationActions = notificationActions;
    }

    setTimeout(async () => {
      await this.initializeCheckBadge(this._lastVisited?.layoutPath, this._lastVisited?.entity);
    }, ArenaPluginManager.DefaultBadgeCheckTimeout);
  }

  private isLayoutVisible(navigationLayout: NavigationLayoutViewModel) {
    const nodes: NavigationNodeBase[] = [navigationLayout.data];
    let group = navigationLayout.group;
    while (group) {
      nodes.push(group.data);
      group = group.group;
    }
    for (const node of nodes) {
      if (node.IsVisible === false || (node.VisibleIndex ?? 0) < 0) {
        return false;
      }
    }
    return true;
  }

  private async disableNotificationInfos(): Promise<void> {
    for (const t of this._notificationInfosTimers) {
      clearInterval(t);
    }
    this._notificationInfosTimers = [];
  }

  private _lastVisited: { layoutPath: string; entity: ICoreDataEntity; timestamp: number };
  private _notificationTimer: NodeJS.Timeout;
  private async notifyCurrentObject(layoutPath: string, entity: ICoreDataEntity): Promise<void> {
    if (this._lastVisited?.entity !== entity) {
      await this.initializeCheckBadge(layoutPath, entity);
    }
    this._lastVisited = { layoutPath: layoutPath, entity: entity, timestamp: Date.now() };
  }

  private async initializeCheckBadge(layoutPath: string, entity: ICoreDataEntity): Promise<void> {
    await this.checkResetBadgeForCurrentObject();
    clearInterval(this._notificationTimer);
    if (entity?.Id) {
      const actions = this._notificationActions
        .get(entity.Id)
        ?.filter((_) => _.navigationLayout.fullName === layoutPath);
      if (actions?.length) {
        this._notificationTimer = setInterval(async () => {
          await this.checkResetBadgeForCurrentObject();
        }, ArenaPluginManager.DefaultBadgeCheckTimeout);
      }
    }
  }

  private async checkResetBadgeForCurrentObject(): Promise<void> {
    const id = this._lastVisited?.entity?.Id;
    if (id && (!this.isArenaBusy || !this.isArenaBusy())) {
      const allactions = this._notificationActions.get(id);
      const actions = allactions?.filter((_) => _.navigationLayout.fullName === this._lastVisited.layoutPath);
      if (actions?.length) {
        let removed = false;
        for (const action of actions) {
          const timeout = action.resetTimeout ?? ArenaPluginManager.DefaultBadgeResetTimeout;
          const timeoutTimestamp = this._lastVisited.timestamp + timeout;
          const currentTimestamp = Date.now();
          if (timeoutTimestamp <= currentTimestamp) {
            CollectionHelper.removeByFilter(allactions, (_) => _.caption === action.caption);
            removed = true;
            try {
              await action.resetAction(this._lastVisited.entity);
              const ids = action.navigationLayout.badgeInfos?.get(action.caption);
              if (ids) {
                if (CollectionHelper.removeItem(ids, id)) {
                  action.navigationLayout.refreshBadgeValue();
                }
              }
            } catch (err) {
              this.abcCore.error.publish(err);
            }
          }
        }
        if (
          removed &&
          !allactions?.filter((_) => _.navigationLayout.fullName === this._lastVisited.layoutPath)?.length
        ) {
          clearInterval(this._notificationTimer);
        }
      }
    }
  }
}
